<template>
  <div class="auction-table">
    <Toast ref="toast" />

    <DataTable
      :value="tableData"
      dataKey="id"
      responsiveLayout="scroll"
      sortField="score"
      :sortOrder="-1"
    >
      <!--      <Column expander style="width: 3rem" />-->

      <!-- ================= АРТЕФАКТ ================= -->
      <Column field="name" header="Артефакт">
        <template #body="{ data }">
          <div
            :class="{
              'row-highlight': highlightMap[data.id]
            }"
            style="white-space: nowrap; position: relative"
          >
            <span @click="copyToClipboard(data.name, 'name')" v-text="data.name"></span>&nbsp;
            <span style="white-space: nowrap" :style="`color: ${getColorQlt(data.qlt)}`">
              {{ data.additionalInfo?.ptn ?? data.ptn }}
            </span>

            <span v-if="highlightMap[data.id]" class="click-indicator">
              👤 {{ highlightMap[data.id].adminId }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="score" header="Score" sortable>
        <template #body="{ data }">
          <span v-tooltip.bottom="artefactScoreTooltip(data.scoreDetails)">
            {{ data.score.toFixed(1) }}
          </span>
        </template>
      </Column>

      <Column field="averagePrice" header="Медиана">
        <template #body="{ data }">
          {{ formatPrice(Number(data.averagePrice)) }}
        </template>
      </Column>

      <Column field="salePrice" header="Цена продажи">
        <template #body="{ data }">
          <div
            :class="{
              'row-highlight': highlightMapPrice[data.id]
            }"
            style="white-space: nowrap; position: relative"
          >
            <span @click="copyToClipboard(data.salePrice, 'price')">{{
              formatPrice(Number(data.salePrice))
            }}</span>

            <span v-if="highlightMapPrice[data.id]" class="click-indicator">
              👤 {{ highlightMapPrice[data.id].adminId }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="currentElems" header="Лоты">
        <template #body="{ data }">
          <div>
            <span v-for="(lot, index) in data.currentElems" :key="index">
              <span>{{ lot.price }}</span>

              <template v-if="lot.additionalInfo?.ptn !== undefined">
                - <span>{{ lot.additionalInfo.ptn }}</span> </template
              >;
            </span>
          </div>
        </template>
      </Column>

      <Column field="currentElems" header="Время">
        <template #body="{ data }">
          <div v-if="data.currentElems.length">
            <div style="white-space: nowrap">
              {{ formatMs(getAuctionTimer(data.startTime, data.endTime).passedMs) }}
            </div>

            <div style="font-size: 12px; white-space: nowrap">
              {{ formatMs(getAuctionTimer(data.startTime, data.endTime).leftMs) }}
            </div>
          </div>
        </template>
      </Column>

      <Column header="Продажи">
        <template #body="{ data }">
          <div>{{ getRecentSales(data, 24) }} / 24ч</div>
          <div class="auction-table__subtle">{{ getRecentSales(data, 48) }} / 48ч</div>
        </template>
      </Column>

      <Column field="historySalePrices" header="Количество проданных">
        <template #body="{ data }">
          <div>
            {{ data.historySalePrices.length }}
          </div>
          <div>
            <HistoryChart
              :historySalePrices="data.historySalePrices"
              :sale-price="data.salePrice"
            />
          </div>
        </template>
      </Column>

      <Column v-if="isBidVariant" field="startPrice" header="Текущая ставка">
        <template #body="{ data }">
          <span v-if="data.isBid">{{ formatPrice(Number(data.startPrice)) }}</span>
        </template>
      </Column>

      <Column v-if="isBidVariant" field="startPrice" header="Ставка на перебив">
        <template #body="{ data }">
          <span v-if="data.isBid" @click="copyToClipboard(calculatedBid(data))">{{
            formatPrice(Number(calculatedBid(data)))
          }}</span>
        </template>
      </Column>

      <Column field="profit" header="Профит">
        <template #body="{ data }">
          {{ formatPrice(Number(data.profit)) }}
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  defineProps,
  withDefaults,
  defineExpose
} from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import { formatPrice } from '@/common/function'
import { getNameArtFromId } from '@/common/list'
import { useWebSocketStore } from '@/store/websocket.store'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import HistoryChart from '@/components/HistoryChart.vue'
import { calculateArtefactScore, artefactScoreTooltip } from '@/common/artefact-score'
import {
  ArtefactFilterSettings,
  DEFAULT_ARTEFACT_FILTER_SETTINGS,
  LiquidityWindow,
  getArtefactFilterReasons,
  getDailySalesRate,
  getRecentSales
} from '@/common/artefact-filters'

const props = withDefaults(
  defineProps<{
    enteredProfit?: number
    isBidVariant?: boolean
    onlyGoodLots?: boolean
    liquidityWindow?: LiquidityWindow
    minLiquiditySales?: number
    minBuyoutProfit?: number
  }>(),
  {
    enteredProfit: 0,
    isBidVariant: false,
    onlyGoodLots: DEFAULT_ARTEFACT_FILTER_SETTINGS.onlyLiquid,
    liquidityWindow: DEFAULT_ARTEFACT_FILTER_SETTINGS.liquidityWindow,
    minLiquiditySales: DEFAULT_ARTEFACT_FILTER_SETTINGS.minSales,
    minBuyoutProfit: DEFAULT_ARTEFACT_FILTER_SETTINGS.minProfit
  }
)

const toast = ref()

const wsStore = useWebSocketStore()

const tableData = ref<any[]>([])

// const expandedRows = ref<Record<string, boolean>>({});

const qltMap: Record<number, string> = {
  0: '#E0E0E0',
  1: '#4CAF50',
  2: '#2196F3',
  3: '#9C27B0',
  4: '#F44336',
  5: '#FFD700'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0] ?? 'white'

interface CurrentLot {
  price: number
  startTime: string
  endTime: string
  currentPrice: number
  additionalInfo: object | null
}

export interface TableRow {
  qlt: number
  ptn: string
  averagePrice: string | null
  historyMinPrice: number
  historySalePrices: HistoryLot[]
  currentPrice: number
  currentElems: CurrentLot[]
  profit: string | null
  commission: number | null
  salePrice: number | null
  startPrice: number | null
  startTime: string | null
  endTime: string | null
  isBid?: boolean
  additionalInfo: object | null
  sales24h?: number
  sales48h?: number
  rawProfit?: number | null
  minRequiredProfit?: number | null
}

interface HistoryLot {
  price: number
  time: string | Date
  additional: object | null
}

function onArtefactProfitAll(data: any) {
  const now = Date.now()
  const next: any[] = []
  const filterSettings: ArtefactFilterSettings = {
    onlyLiquid: props.onlyGoodLots,
    liquidityWindow: props.liquidityWindow,
    minSales: props.minLiquiditySales,
    minProfit: props.minBuyoutProfit
  }

  for (const artefactId in data.artefactProfitMap) {
    const artefact = data.artefactProfitMap[artefactId]
    if (!artefact?.rows) continue

    const name = getNameArtFromId(artefactId)

    artefact.rows.forEach((row: any, index: number) => {
      const scoreDetails = calculateArtefactScore(row, props.liquidityWindow, now)
      const item = {
        ...row,
        score: scoreDetails.score,
        scoreDetails,
        name,
        id: `${artefactId}_${index}`
      }

      if (getArtefactFilterReasons(row, filterSettings, now).length) return

      item._dailySalesRate = getDailySalesRate(row, props.liquidityWindow, now)

      next.push(item)
    })
  }

  next.sort((a, b) => {
    return (
      b.score - a.score ||
      Number(b.rawProfit ?? b.profit) - Number(a.rawProfit ?? a.profit) ||
      b._dailySalesRate - a._dailySalesRate ||
      a.id.localeCompare(b.id)
    )
  })

  tableData.value = next.filter(i => !i.isBid)
}

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string, type: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })

  wsStore.sendArt({
    text,
    type
  })
}

function getAuctionTimer(startIso: string, endIso: string) {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const now = Date.now()

  // const duration = end - start;
  const passed = Math.max(0, now - start)
  const left = Math.max(0, end - now)

  return {
    passedMs: passed,
    leftMs: left
  }
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)

  if (h > 0) return `${h}ч ${m % 60}м`
  if (m > 0) return `${m}м ${s % 60}с`
  return `${s}с`
}

function calculatedBid(data) {
  const firstElemPrice = data.currentElems[0]?.price

  let salePrice = firstElemPrice <= data.averagePrice ? firstElemPrice - 1 : data.averagePrice

  const commission = salePrice * 0.05
  const result = salePrice - props.enteredProfit - commission

  return Math.floor(result)
}

const isBid = ref(false)

const highlightMap = ref<Record<string, { adminId: number; until: number }>>({})

const highlightMapPrice = ref<Record<string, { adminId: number; until: number }>>({})

function onAdminClick(data) {
  if (data.type === 'name') {
    const item = tableData.value.find(i => i.name === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMap.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMap.value[item.id]
    }, duration)
  } else if (data.type === 'price') {
    const item = tableData.value.find(i => i.salePrice === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMapPrice.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMapPrice.value[item.id]
    }, duration)
  }
}

watch(
  [
    () => wsStore.artefactProfitData,
    () => props.onlyGoodLots,
    () => props.liquidityWindow,
    () => props.minLiquiditySales,
    () => props.minBuyoutProfit
  ],
  ([data]) => {
    onArtefactProfitAll(data)
  },
  { immediate: true }
)

onMounted(() => {
  wsStore.connect() // 👈 ВАЖНО

  wsStore.artefactSocket?.on('admin_click', onAdminClick)

  isBid.value = localStorage.getItem('bid') === 'true'
})

onBeforeUnmount(() => {
  wsStore.artefactSocket?.off('admin_click', onAdminClick)
  tableData.value = []
})
</script>

<style lang="scss" scoped>
body {
  background-color: #1e1e1e;
  color: #f0e6d2;
  font-family: 'Arial', sans-serif;
  margin: 0;
  padding: 0;
}

.auction-table {
  width: 95%;
  margin: 20px auto;
  background-color: #2c2c2c;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  padding: 20px;
}

.auction-table__subtle {
  margin-top: 2px;
  color: #999999;
  font-size: 11px;
}

.artifact-info {
  font-size: 12px;
  line-height: 1.4;
}

.row {
  display: flex;
  gap: 8px;
}

.key {
  min-width: 140px;
  color: #9aa0a6;
}

.value {
  word-break: break-word;
}

.row-highlight {
  animation: flash 0.6s ease-in-out 3;
  background-color: rgba(255, 255, 0, 0.25);
}

@keyframes flash {
  0% {
    background-color: rgba(255, 255, 0, 0.1);
  }
  50% {
    background-color: rgba(255, 200, 0, 0.5);
  }
  100% {
    background-color: rgba(255, 255, 0, 0.1);
  }
}

.click-indicator {
  position: absolute;
  left: 0;
  top: 20px;
  font-size: 11px;
  color: #ff9800;
}
</style>
